import axios from 'axios'
import { api } from '@/app/services/api.service'
import jwtService from '@/app/services/jwt.service'
import abilities from '@/abilities'
import { useLoginStore } from '@/stores/login'
import { persistRefreshTokenExpiryTime, touchSession } from '@/composables/tokenRefresh'

export { sessionRevision, touchSession } from '@/composables/tokenRefresh'

type GlobalProperties = Record<string, unknown>

let globalProperties: GlobalProperties | null = null

export function bindAuthGlobalProperties(props: GlobalProperties) {
  globalProperties = props
}

function stripAuthorizationHeader(headers: unknown): void {
  if (!headers || typeof headers !== 'object') return

  const candidate = headers as {
    delete?: (name: string) => void
    common?: unknown
  }

  if (typeof candidate.delete === 'function') {
    candidate.delete('Authorization')
    candidate.delete('authorization')
  }

  if (!Array.isArray(headers)) {
    delete (headers as Record<string, unknown>).Authorization
    delete (headers as Record<string, unknown>).authorization
  }

  if (candidate.common) {
    stripAuthorizationHeader(candidate.common)
  }
}

function mergeStoredUser(userDetails: Record<string, unknown>) {
  let existing: Record<string, unknown> = {}
  try {
    existing = jwtService.getUser() || {}
  } catch {
    existing = {}
  }

  const merged = { ...existing, ...userDetails }
  if (userDetails.profileComplete === undefined && existing.profileComplete !== undefined) {
    merged.profileComplete = existing.profileComplete
  }
  return merged
}

function mergeStoredUserProfileKey(userDetails: Record<string, unknown>) {
  const raw = sessionStorage.getItem('userProfile')
  if (!raw) return

  try {
    const existing = JSON.parse(raw)
    if (!existing || typeof existing !== 'object') return
    const merged = { ...existing, ...userDetails }
    if (userDetails.profileComplete === undefined && existing.profileComplete !== undefined) {
      merged.profileComplete = existing.profileComplete
    }
    sessionStorage.setItem('userProfile', JSON.stringify(merged))
  } catch {
    // ignore malformed stored profile
  }
}

function syncAbilities(userDetails: { roles?: Array<{ permissions: Array<{ permission: string }> }> }) {
  if (!userDetails?.roles) return

  const permissions: Array<{ action: string; resource: string }> = []
  userDetails.roles.forEach((role) => {
    role.permissions.forEach((permission) => {
      permissions.push({
        action: permission.permission,
        resource: 'general',
      })
    })
  })

  sessionStorage.setItem('abilities', JSON.stringify(permissions))
  abilities.update(permissions)
}

export async function refreshSessionToken(refreshToken: string) {
  const client = api()
  stripAuthorizationHeader(client.defaults.headers)

  const interceptorId = client.interceptors.request.use((config) => {
    stripAuthorizationHeader(config.headers)
    return config
  })

  try {
    const response = await client.post('auth/api/auth/refresh-token', { refreshToken })
    const data = response.data
    const result = data.data || data

    const newAccessToken = result.accessToken || result.token
    const newRefreshToken = result.refreshToken || result.tokenRefresh
    const userDetails = result.user

    if (newAccessToken) {
      jwtService.saveToken(newAccessToken)
      axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
    }

    if (newRefreshToken) {
      sessionStorage.setItem('tokenRefresh', newRefreshToken)
    }

    persistRefreshTokenExpiryTime(
      result.refreshTokenExpiryTime ?? userDetails?.refreshTokenExpiryTime,
    )

    if (userDetails) {
      const mergedUser = mergeStoredUser(userDetails)
      jwtService.saveUser(mergedUser)
      mergeStoredUserProfileKey(userDetails)
      useLoginStore().setCurrentUserProfile(mergedUser)

      if (globalProperties) {
        globalProperties.$userProfile = mergedUser
      }

      syncAbilities(mergedUser)
    }

    touchSession()
    return result
  } finally {
    client.interceptors.request.eject(interceptorId)
  }
}
